import { useEffect, useRef, useState } from 'react';
import { OfflineNotice, SessionExpiredModal, ToastStack } from '../components/common';
import { ConfirmationModal, Header, ProfileSettingsModal, Sidebar } from '../components/dashboard';
import useNavigationNotifications from '../hooks/useNavigationNotifications';
import useOnlineStatus from '../hooks/useOnlineStatus';
import useServerStatus from '../hooks/useServerStatus';
import {
  fetchPortalContext,
  getCachedPortalContext,
  updateCachedPortalUser,
} from '../services/portalContextService';
import { PORTAL_SESSION_EXPIRED_EVENT } from '../services/apiConfig';
import {
  clearBusinessContext,
  getPortalLogoutUrl,
  getPortalRole,
  getPortalReloginUrl,
  getPortalReturnUrl,
  isPortalBackActionHidden,
} from './businessContext';
import {
  DEFAULT_BUSINESS_THEME,
  getBusinessThemeName,
  getBusinessThemeStyle,
} from './businessTheme';

let hasSeenDisconnectedState = false;
const SIDEBAR_COLLAPSE_STORAGE_KEY = 'seaweeds.sidebar.collapsed';

function AppShell({ navigationItems, activePath, activeSearch = '', onNavigate, children }) {
  const isOnline = useOnlineStatus();
  const isServerOnline = useServerStatus();
  const navigationBadges = useNavigationNotifications();
  const isConnected = isOnline && isServerOnline === true;
  const wasConnectedRef = useRef(isConnected);
  const forcedPasswordPromptRef = useRef(false);
  const [connectionToast, setConnectionToast] = useState(null);
  const [accountToast, setAccountToast] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
  const [isSessionExpiredModalOpen, setIsSessionExpiredModalOpen] = useState(false);
  const [hasLoadedPortalUser, setHasLoadedPortalUser] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY) === 'true';
  });
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );
  const cachedPortalContext = getCachedPortalContext();
  const [portalUser, setPortalUser] = useState(cachedPortalContext?.user || null);
  const [businessTheme, setBusinessTheme] = useState(
    getBusinessThemeName(cachedPortalContext?.business?.theme || DEFAULT_BUSINESS_THEME)
  );
  const previousViewportWidthRef = useRef(viewportWidth);
  const isStaffView = activePath?.includes('/staff/');
  const hideBackAction = isPortalBackActionHidden();
  const actionLabel = 'Back to e-Portal';
  const portalRole = getPortalRole();
  const shouldShowHeaderAction =
    !isStaffView && !hideBackAction && portalRole !== 'accountant';
  const accountLabel =
    portalUser?.username ||
    (portalRole === 'staff'
      ? 'Staff'
      : portalRole === 'admin-custom'
        ? 'Admin (custom)'
        : portalRole === 'accountant'
          ? 'Accountant'
          : portalRole === 'admin'
            ? 'Admin'
            : 'Account');
  const isMediumViewport = viewportWidth >= 768;
  const isLargeViewport = viewportWidth >= 1024;
  const effectiveSidebarCollapsed = !isMediumViewport ? false : isSidebarCollapsed;
  const appVersion = process.env.REACT_APP_VERSION || 'v0.1.0';

  useEffect(() => {
    if (!isConnected && isServerOnline !== null) {
      hasSeenDisconnectedState = true;
    }

    if (hasSeenDisconnectedState && !wasConnectedRef.current && isConnected) {
      setConnectionToast({
        id: `connection-restored-${Date.now()}`,
        tone: 'success',
        message: 'Server is online now. You are connected again.',
      });
      hasSeenDisconnectedState = false;
    }

    wasConnectedRef.current = isConnected;
  }, [isConnected, isServerOnline]);

  useEffect(() => {
    if (!connectionToast) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setConnectionToast(null);
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [connectionToast]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    function handlePortalSessionExpired() {
      setIsSessionExpiredModalOpen(true);
    }

    window.addEventListener(PORTAL_SESSION_EXPIRED_EVENT, handlePortalSessionExpired);

    return () => {
      window.removeEventListener(PORTAL_SESSION_EXPIRED_EVENT, handlePortalSessionExpired);
    };
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    function handleResize() {
      setViewportWidth(window.innerWidth);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const previousViewportWidth = previousViewportWidthRef.current;
    const wasMediumViewport =
      previousViewportWidth >= 768 && previousViewportWidth < 1024;

    if (isMediumViewport && !isLargeViewport && !wasMediumViewport) {
      setIsSidebarCollapsed(true);
    }

    previousViewportWidthRef.current = viewportWidth;
  }, [isLargeViewport, isMediumViewport, viewportWidth]);

  useEffect(() => {
    let isMounted = true;

    async function loadPortalUser() {
      try {
        const portalContext = await fetchPortalContext({ force: true });
        const nextUser = portalContext?.user || null;
        const nextTheme = getBusinessThemeName(
          portalContext?.business?.theme || DEFAULT_BUSINESS_THEME
        );

        if (isMounted) {
          setPortalUser(nextUser);
          setBusinessTheme((currentTheme) =>
            currentTheme === nextTheme ? currentTheme : nextTheme
          );
        }
      } catch (error) {
        // Keep the cached theme/user on slow or failed e-Portal requests so the UI
        // does not flash back to the default colors during startup.
      } finally {
        if (isMounted) {
          setHasLoadedPortalUser(true);
        }
      }
    }

    loadPortalUser();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const themeStyle = getBusinessThemeStyle(businessTheme);
    const rootStyle = document.documentElement.style;
    const previousValues = Object.keys(themeStyle).reduce((values, propertyName) => {
      values[propertyName] = rootStyle.getPropertyValue(propertyName);
      rootStyle.setProperty(propertyName, themeStyle[propertyName]);
      return values;
    }, {});

    document.documentElement.dataset.businessTheme = businessTheme;

    return () => {
      Object.entries(previousValues).forEach(([propertyName, propertyValue]) => {
        if (propertyValue) {
          rootStyle.setProperty(propertyName, propertyValue);
        } else {
          rootStyle.removeProperty(propertyName);
        }
      });

      delete document.documentElement.dataset.businessTheme;
    };
  }, [businessTheme]);

  useEffect(() => {
    if (!hasLoadedPortalUser) {
      return;
    }

    if (portalUser?.mustChangePassword) {
      forcedPasswordPromptRef.current = true;
      setIsProfileSettingsOpen(true);
      return;
    }

    if (forcedPasswordPromptRef.current) {
      forcedPasswordPromptRef.current = false;
      setIsProfileSettingsOpen(false);
    }
  }, [hasLoadedPortalUser, portalUser?.mustChangePassword]);

  useEffect(() => {
    if (!accountToast) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setAccountToast(null);
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [accountToast]);

  function handleHeaderAction() {
    clearBusinessContext();
    window.location.href = getPortalReturnUrl();
  }

  function handleLogout() {
    setIsLogoutModalOpen(false);
    const logoutUrl = getPortalLogoutUrl();

    clearBusinessContext();
    window.location.href = logoutUrl;
  }

  function handleRelogin() {
    const reloginUrl = getPortalReloginUrl(activePath);

    clearBusinessContext();
    window.location.href = reloginUrl;
  }

  function handleNavigate(nextPath) {
    onNavigate(nextPath);
    setIsMobileMenuOpen(false);
  }

  return (
    <div
      className="min-h-[100dvh] min-w-0 bg-[var(--theme-app-bg)] text-[var(--theme-text)] md:grid md:h-[100dvh] md:overflow-hidden md:transition-[grid-template-columns] md:duration-500 md:ease-[cubic-bezier(0.22,1,0.36,1)]"
      data-business-theme={businessTheme}
      style={
        isMediumViewport
          ? {
              ...getBusinessThemeStyle(businessTheme),
              gridTemplateColumns: effectiveSidebarCollapsed
                ? '96px minmax(0, 1fr)'
                : '230px minmax(0, 1fr)',
            }
          : getBusinessThemeStyle(businessTheme)
      }
    >
      <Sidebar
        navigationItems={navigationItems}
        activePath={activePath}
        activeSearch={activeSearch}
        navigationBadges={navigationBadges}
        isCollapsed={effectiveSidebarCollapsed}
        version={appVersion}
        isMobileMenuOpen={isMobileMenuOpen}
        onToggleCollapse={() => setIsSidebarCollapsed((isCollapsed) => !isCollapsed)}
        onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
        onNavigate={handleNavigate}
      />

      <main className="flex min-w-0 flex-col md:h-full md:min-h-0 md:overflow-hidden">
        <ToastStack
          toasts={[connectionToast, accountToast].filter(Boolean)}
          onDismiss={(toastId) => {
            if (toastId === connectionToast?.id) {
              setConnectionToast(null);
            }

            if (toastId === accountToast?.id) {
              setAccountToast(null);
            }
          }}
        />

        <div className="sticky top-0 z-20 max-w-full border-b border-[var(--theme-border-soft)] bg-[color-mix(in_srgb,var(--theme-surface-warm)_96%,white)] shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.06)] backdrop-blur-[12px]">
          <div className="min-w-0 max-w-full px-4 sm:px-6 md:px-[26px]">
            <Header
              actionLabel={actionLabel}
              accountAvatar={portalUser?.avatar || ''}
              accountLabel={accountLabel}
              showAction={shouldShowHeaderAction}
              isMobileMenuOpen={isMobileMenuOpen}
              onAction={handleHeaderAction}
              onLogout={() => setIsLogoutModalOpen(true)}
              onMenuToggle={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
              onOpenSettings={() => setIsProfileSettingsOpen(true)}
            />
          </div>
        </div>

        {!isOnline ? (
          <div className="mt-4 px-4 sm:px-6 md:px-[26px]">
            <OfflineNotice />
          </div>
        ) : null}

        <div
          className="bg-white pb-[calc(1.75rem+env(safe-area-inset-bottom))] md:min-h-0 md:flex-1 md:overflow-y-auto md:overflow-x-hidden md:pb-8"
          data-app-scroll-container="true"
        >
          {children}
        </div>
      </main>

      <ConfirmationModal
        confirmLabel="Logout"
        description="Are you sure you want to log out of the current session?"
        isOpen={isLogoutModalOpen}
        onCancel={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
        title="Confirm Logout"
      />
      <ProfileSettingsModal
        isOpen={isProfileSettingsOpen}
        user={portalUser}
        forcePasswordChange={Boolean(portalUser?.mustChangePassword)}
        onClose={() => {
          if (!portalUser?.mustChangePassword) {
            forcedPasswordPromptRef.current = false;
            setIsProfileSettingsOpen(false);
          }
        }}
        onUserChange={(nextUser, event = {}) => {
          setPortalUser(nextUser);
          updateCachedPortalUser(nextUser);

          if (event.passwordChanged) {
            setAccountToast({
              id: `password-changed-${Date.now()}`,
              tone: 'success',
              message: event.message || 'Password updated successfully.',
            });
          }

          if (!nextUser?.mustChangePassword) {
            forcedPasswordPromptRef.current = false;
            setIsProfileSettingsOpen(false);
          }
        }}
      />
      <SessionExpiredModal isOpen={isSessionExpiredModalOpen} onRelogin={handleRelogin} />
    </div>
  );
}

export default AppShell;
